import type { ReactNode } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/auth-context";
import { cn } from "@/lib/cn";
import { MarketingLocaleSwitcher } from "./locale-switcher";

/**
 * Shared chrome for public ZonaClara pages (home + equipment).
 *
 * Kept out of AppLayout on purpose: marketing is anonymous and must not
 * inherit the authenticated shell. Tokens live under `.marketing-root`.
 */
export function MarketingChrome({
  children,
  active,
}: {
  children: ReactNode;
  active: "home" | "equipment";
}) {
  const { t } = useTranslation("marketing");
  const { status } = useAuth();
  const signedIn = status === "authenticated";

  return (
    <div className="marketing-root marketing-page min-h-dvh">
      <header className="marketing-chrome-header">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="marketing-brand-mark text-xl text-ink sm:text-2xl">
            {t("brand")}
          </Link>
          <nav className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2" aria-label={t("brand")}>
            <Link
              to="/"
              className={cn(
                "marketing-nav-link hidden lg:inline",
                active === "home" && "font-semibold",
              )}
              aria-current={active === "home" ? "page" : undefined}
            >
              {t("navHome")}
            </Link>
            <Link to="/#usos" className="marketing-nav-link hidden lg:inline">
              {t("navUses")}
            </Link>
            <Link
              to="/equipos"
              className={cn(
                "marketing-nav-link hidden lg:inline",
                active === "equipment" && "font-semibold",
              )}
              aria-current={active === "equipment" ? "page" : undefined}
            >
              {t("navEquipment")}
            </Link>
            <Link to="/#contact" className="marketing-nav-link hidden lg:inline">
              {t("navContact")}
            </Link>
            <MarketingLocaleSwitcher />
            {signedIn ? (
              <Link to="/dashboard" className="marketing-cta marketing-cta-primary px-3 py-2 text-sm">
                {t("navDashboard")}
              </Link>
            ) : (
              <Link to="/login" className="marketing-cta marketing-cta-primary px-3 py-2 text-sm">
                {t("navLogin")}
              </Link>
            )}
          </nav>
        </div>
      </header>

      {children}

      <footer className="border-t border-border-subtle bg-[var(--marketing-navy-deep)] px-4 py-7 text-center text-xs text-white/65 sm:px-6">
        {t("footerNote")}
      </footer>
    </div>
  );
}
